// BOMs: every family (Shopify product or sub-assembly) at its current revision, with its cost against
// the Shopify price and what needs fixing. ?open=<bom id> shows the editor; ?project= filters.
import { AlertTriangle, Layers, Replace } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { type FamilySummary, families, revisionState, summarizeFamily } from "../../../shared/boms.ts";
import { costRange, createBom, marginTone, pctRange, STATE_TONE, unitsRange, useBomIndex } from "../lib/boms.ts";
import { qty, skusText } from "../lib/format.ts";
import { can } from "../lib/mutate.ts";
import { bomProjects, useProjectIndex } from "../lib/projects.ts";
import { setSearch, useSearch } from "../lib/router.tsx";
import { useRows } from "../lib/sync.ts";
import { Badge, Button, cx, EmptyState, Input, Select } from "../ui/controls.tsx";
import type { Column } from "../ui/DataTable.tsx";
import { DialogLayer } from "../ui/DialogLayer.tsx";
import { ListPage } from "../ui/ListPage.tsx";
import { BomEditor } from "./BomEditor.tsx";
import { ReplacePartDialog } from "./BomReplace.tsx";

type Row = FamilySummary & { id: string };

export function BomsPage() {
  const id = useSearch().get("open");
  return id ? <BomEditor key={id} id={id} /> : <BomDashboard />;
}

function BomDashboard() {
  const ix = useBomIndex();
  const px = useProjectIndex();
  const projects = useRows("project");
  const vendors = useRows("vendor");
  const params = useSearch();
  const project = params.get("project") ?? "";
  const [creating, setCreating] = useState(false);
  const [replacing, setReplacing] = useState(false);

  const rows = useMemo<Row[]>(() => families(ix).map((b) => ({ ...summarizeFamily(ix, b), id: b.id })), [ix]);
  // Manufacturing projects --- the projects each family is built in (bomProjects()): a product's link or its BOM's
  // project, a kit nothing uses by its own, a used sub-assembly by what uses it (a shared board under both), and a
  // sub-assembly only a newer draft uses under that draft's project, "(draft)", as the Parts library has it
  const shown = useMemo(() => {
    if (!project) return rows;
    return rows.filter((r) => {
      const p = bomProjects(px, r.bom);
      return p.inUse.includes(project) || p.onDraft.includes(project);
    });
  }, [rows, project, px]);

  const columns = useMemo<Column<Row>[]>(() => {
    const projectName = new Map(projects.map((p) => [p.id, p.name]));
    const projectsText = (r: Row) => {
      const p = bomProjects(px, r.bom);
      return [...p.inUse.map((id) => projectName.get(id) ?? ""), ...p.onDraft.map((id) => `${projectName.get(id) ?? ""} (draft)`)].join(", ");
    };
    const vendorName = new Map(vendors.map((v) => [v.id, v.short_name || v.name]));
    const flagText = (r: Row) =>
      [
        r.flags.draft && "draft",
        r.flags.missingCost.length && `${r.flags.missingCost.length} no cost`,
        r.flags.missingVendor.length && `${r.flags.missingVendor.length} no vendor`,
        r.flags.noMakeVendor && "no CM",
        r.flags.endOfLife.length && `${r.flags.endOfLife.length} EOL`,
        r.flags.warnings.length && `${r.flags.warnings.length} warnings`,
      ]
        .filter(Boolean)
        .join(" ");
    return [
      // SKU, then Name, as every table of items starts
      {
        key: "sku",
        header: "SKU",
        width: "minmax(110px,150px)",
        value: (r) => skusText(r.variants.map((v) => v.variant.sku)),
        cell: (r) => <span className="truncate font-mono text-[12px]">{skusText(r.variants.map((v) => v.variant.sku))}</span>,
      },
      { key: "label", header: "Name", width: "minmax(240px,2fr)", cell: (r) => <span className="truncate font-medium">{r.label}</span> },
      {
        key: "kind",
        header: "Type",
        width: "170px",
        value: (r) => (r.bom.shopify_product_id ? "Product" : "Sub-assembly"),
        cell: (r) =>
          r.bom.shopify_product_id ? (
            <span className="text-muted">Product</span>
          ) : (
            <span className="flex items-center gap-1 text-muted">
              Sub-assembly {(r.bom.phantom || !r.bom.make_vendor_id) && <Badge>built in the same order</Badge>}
            </span>
          ),
      },
      {
        key: "project",
        header: "Projects",
        width: "150px",
        value: projectsText,
        cell: (r) => <span className="truncate">{projectsText(r)}</span>,
      },
      {
        key: "revision",
        header: "Revision",
        width: "130px",
        value: (r) => r.bom.revision,
        cell: (r) => {
          const state = revisionState(ix, r.bom);
          return (
            <span className="flex items-center gap-1.5">
              Rev {r.bom.revision} <Badge tone={STATE_TONE[state]}>{state}</Badge>
            </span>
          );
        },
      },
      { key: "variants", header: "Variants", width: "80px", align: "right", value: (r) => r.variants.length },
      {
        key: "cost",
        header: "Cost",
        width: "150px",
        align: "right",
        value: (r) => r.cost?.min ?? null,
        cell: (r) => (
          <span className="flex items-center gap-1">
            {r.flags.missingCost.length > 0 && (
              <span title={`Missing costs: ${r.flags.missingCost.map((i) => i.sku || i.name).join(", ")}`}>
                <AlertTriangle className="size-3.5 text-warn" />
              </span>
            )}
            {costRange(r.cost)}
          </span>
        ),
      },
      { key: "price", header: "Price", width: "130px", align: "right", value: (r) => r.price?.min ?? null, cell: (r) => costRange(r.price) },
      {
        key: "build",
        header: "Can build",
        width: "170px",
        align: "right",
        value: (r) => r.buildable?.min ?? null,
        cell: (r) => (
          <span
            className="flex min-w-0 items-center justify-end gap-1.5"
            title={
              r.limiting
                ? `Limited by ${r.limiting.item.sku || r.limiting.item.name}: ${qty(r.limiting.onHand)} available, ${qty(r.limiting.perUnit)} per unit`
                : "From stock available at HHL (on hand less units committed to customer orders); parts the CM buys don't count"
            }
          >
            {r.limiting && <span className="truncate font-mono text-[11px] text-faint">{r.limiting.item.sku}</span>}
            <span className={cx("font-mono", r.buildable?.min === 0 && "text-warn")}>{unitsRange(r.buildable)}</span>
          </span>
        ),
      },
      {
        key: "margin",
        header: "Margin",
        width: "100px",
        align: "right",
        value: (r) => r.margin?.min ?? null,
        cell: (r) => <span className={marginTone(r.margin?.min)}>{pctRange(r.margin) || (r.price ? "—" : "")}</span>,
      },
      {
        key: "flags",
        header: "Needs",
        width: "minmax(220px,1.5fr)",
        value: flagText,
        cell: (r) => (
          <span className="flex gap-1">
            {r.flags.draft && <Badge tone="accent">Draft</Badge>}
            {r.flags.missingCost.length > 0 && <Badge tone="warn">{r.flags.missingCost.length} no cost</Badge>}
            {r.flags.missingVendor.length > 0 && <Badge tone="warn">{r.flags.missingVendor.length} no vendor</Badge>}
            {r.flags.noMakeVendor && <Badge>No CM</Badge>}
            {r.flags.endOfLife.length > 0 && <Badge tone="danger">{r.flags.endOfLife.length} EOL</Badge>}
            {r.flags.warnings.length > 0 && <Badge tone="danger">{r.flags.warnings.length} warnings</Badge>}
          </span>
        ),
      },
      { key: "cm", header: "CM", width: "120px", value: (r) => vendorName.get(r.bom.make_vendor_id ?? "") ?? "" },
    ];
  }, [ix, projects, vendors, px]);

  return (
    <>
      <ListPage
        title="BOMs"
        rows={shown}
        columns={columns}
        defaultSort={{ key: "label" }}
        newLabel="New BOM"
        onNew={can("bom.create") ? () => setCreating(true) : undefined}
        panel={() => null}
        toolbar={
          <div className="flex items-center gap-2 [[data-stacked]_&]:flex-wrap">
            <div className="w-48">
              <Select
                value={project}
                onChange={(e) => setSearch({ project: e.target.value })}
                options={[{ value: "", label: "All projects" }, ...projects.map((p) => ({ value: p.id, label: p.name }))]}
              />
            </div>
            {can("bom.replace_part") && (
              <Button icon={<Replace className="size-3.5" />} onClick={() => setReplacing(true)} title="Swap one part for another in the BOMs that use it">
                Replace part
              </Button>
            )}
          </div>
        }
        empty={
          <EmptyState icon={<Layers className="size-8" />} title={rows.length ? "No BOMs in this project" : "No BOMs yet"}>
            Press N to start one for a Shopify product (one BOM for all its variants) or a sub-assembly.
          </EmptyState>
        }
      />
      {creating && <NewBomDialog onClose={() => setCreating(false)} />}
      {replacing && <ReplacePartDialog onClose={() => setReplacing(false)} />}
    </>
  );
}

/** Pick what the BOM is for: a Shopify product or a sub-assembly. Ones that already have a BOM open it. */
function NewBomDialog({ onClose }: { onClose: () => void }) {
  const ix = useBomIndex();
  const products = useRows("shopify_product");
  const items = useRows("item");
  const [kind, setKind] = useState<"product" | "item">("product");
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const busy = useRef(false);

  const options = useMemo(() => {
    const all =
      kind === "product"
        ? products
            .filter((p) => !p.deleted && !p.skip_reason)
            .map((p) => ({ key: `product:${p.id}`, owner: { shopify_product_id: p.id }, label: p.title, detail: `${p.variant_count} variants` }))
        : items
            .filter((i) => i.kind === "subassembly" && !i.archived)
            .map((i) => ({ key: `item:${i.id}`, owner: { item_id: i.id }, label: i.name, detail: i.sku }));
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    return all
      .filter((o) => terms.every((t) => `${o.label} ${o.detail}`.toLowerCase().includes(t)))
      .sort((a, b) => a.label.localeCompare(b.label))
      .slice(0, 80);
  }, [kind, products, items, query]);

  const choose = async (o: (typeof options)[number] | undefined) => {
    if (!o || busy.current) return;
    const existing = ix.families.get(o.key)?.[0];
    if (existing) {
      setSearch({ open: existing.id });
      return onClose();
    }
    busy.current = true;
    if (await createBom(ix, o.owner)) onClose();
    busy.current = false;
  };

  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="flex max-h-[70vh] w-[520px] flex-col rounded-xl border border-line bg-surface p-4 shadow-pop"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <div className="mb-3 flex items-center gap-3">
          <div className="text-[15px] font-semibold">New BOM</div>
          <div className="ml-auto flex rounded-md border border-line p-0.5 text-[12px]">
            {(["product", "item"] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setKind(k);
                  setCursor(0);
                }}
                className={cx("rounded px-2 py-0.5", kind === k ? "bg-accent-soft text-accent-text" : "text-muted hover:text-text")}
              >
                {k === "product" ? "Shopify product" : "Sub-assembly"}
              </button>
            ))}
          </div>
        </div>
        <Input
          autoFocus
          value={query}
          placeholder={kind === "product" ? "Find a product…" : "Find a sub-assembly…"}
          onChange={(e) => {
            setQuery(e.target.value);
            setCursor(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") setCursor((c) => Math.min(options.length - 1, c + 1));
            else if (e.key === "ArrowUp") setCursor((c) => Math.max(0, c - 1));
            else if (e.key === "Enter") choose(options[cursor]);
            else if (e.key === "Tab") setKind((k) => (k === "product" ? "item" : "product"));
            else return;
            e.preventDefault();
          }}
        />
        <div className="mt-2 min-h-0 flex-1 overflow-y-auto">
          {options.length === 0 && (
            <div className="p-3 text-muted">{kind === "item" ? "No sub-assemblies match. Add one under Sub-assemblies first." : "No products match."}</div>
          )}
          {options.map((o, i) => (
            <button
              key={o.key}
              type="button"
              onMouseEnter={() => setCursor(i)}
              onClick={() => choose(o)}
              className={cx("flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left", i === cursor && "bg-hover")}
            >
              <span className="min-w-0 flex-1 truncate">{o.label}</span>
              <span className="shrink-0 font-mono text-[12px] text-muted">{o.detail}</span>
              {ix.families.has(o.key) && <Badge tone="ok">Has a BOM</Badge>}
            </button>
          ))}
        </div>
        <div className="mt-2 text-[11px] text-faint">↑↓ to move · Enter to create (or open) · Tab switches products / sub-assemblies</div>
      </div>
    </DialogLayer>
  );
}
